import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelProjectOwnershipTransfer,
  CancelProjectOwnershipTransferInput,
  getProjectOwnershipTransfer,
  GetProjectOwnershipTransferInput,
  initiateProjectOwnershipTransfer,
  InitiateProjectOwnershipTransferInput,
  resendProjectOwnershipTransferEmail,
  ResendProjectOwnershipTransferEmailInput,
  updateProjectOwnershipTransferEmail,
  UpdateProjectOwnershipTransferEmailInput,
} from '@/features/projects/project-details/api/project-ownership-transfer';
import { getProjectDetailsQueryKey } from '@/features/projects/shared/hooks/use-project-details';

export function getProjectOwnershipTransferQueryKey(projectId: number) {
  return ['project-ownership-transfer', projectId];
}

export function useProjectOwnershipTransfer({
  projectId,
}: GetProjectOwnershipTransferInput) {
  return useQuery({
    queryKey: getProjectOwnershipTransferQueryKey(projectId),
    queryFn: () => getProjectOwnershipTransfer({ projectId }),
    enabled: !!projectId,
  });
}

export function useInitiateProjectOwnershipTransferMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InitiateProjectOwnershipTransferInput) =>
      initiateProjectOwnershipTransfer(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getProjectOwnershipTransferQueryKey(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getProjectDetailsQueryKey(variables.projectId.toString()),
      });
    },
  });
}

export function useResendProjectOwnershipTransferEmailMutation(
  projectId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ResendProjectOwnershipTransferEmailInput) =>
      resendProjectOwnershipTransferEmail(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProjectOwnershipTransferQueryKey(projectId),
      });
    },
  });
}

export function useUpdateProjectOwnershipTransferEmailMutation(
  projectId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProjectOwnershipTransferEmailInput) =>
      updateProjectOwnershipTransferEmail(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProjectOwnershipTransferQueryKey(projectId),
      });
    },
  });
}

export function useCancelProjectOwnershipTransferMutation(projectId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CancelProjectOwnershipTransferInput) =>
      cancelProjectOwnershipTransfer(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProjectOwnershipTransferQueryKey(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getProjectDetailsQueryKey(projectId.toString()),
      });
    },
  });
}
